<template>
  <v-btn
    :text="text || computedProps?.text || false"
    :outlined="outlined || computedProps.outlined || false"
    :class="`${
      textType || computedProps.textType
        ? `text-${textType || computedProps.textType}`
        : ''
    } ${
      rounded || computedProps.rounded
        ? `rounded-${rounded || computedProps.rounded}`
        : ''
    } ${
      fontWeight || computedProps.fontWeight
        ? `font-weight-${fontWeight || computedProps.fontWeight}`
        : ''
    } ${rounded === 'pill' || computedProps.rounded === 'pill' ? `px-5` : ''} ${
      dense || computedProps.dense ? 'dense' : ''
    } ${stacked || computedProps.stacked ? 'stacked' : ''} ${
      textColor || computedProps.textColor
        ? `${textColor || computedProps.textColor}--text`
        : ''
    } ${
      (color || computedProps.color) && (outlined || computedProps.outlined)
        ? `${color || computedProps.color}`
        : ''
    } ${!ripple || !computedProps.ripple ? 'flat' : ''} ${extraClasses ?? ''}`"
    class="styled-button"
    :depressed="depressed ?? computedProps.depressed"
    :ripple="ripple ?? computedProps.ripple"
    :block="block ?? computedProps.block"
    @click="handleAction"
    :loading="loading"
    :xSmall="
      size === 'xSmall' || (computedProps.size === 'xSmall' && !size?.length)
    "
    :small="
      size === 'small' || (computedProps.size === 'small' && !size?.length)
    "
    :large="
      size === 'large' || (computedProps.size === 'large' && !size?.length)
    "
    :xLarge="
      size === 'xLarge' || (computedProps.size === 'xLarge' && !size?.length)
    "
    :color="color || computedProps.color || undefined"
    :to="to ?? undefined"
    :min-height="minHeight ?? computedProps?.minHeight"
    :height="height ?? computedProps?.height"
    :max-height="maxHeight ?? computedProps?.maxHeight"
    :min-width="minWidth ?? computedProps?.minWidth"
    :width="width ?? computedProps?.width"
    :max-width="maxWidth ?? computedProps?.maxWidth"
    :disabled="disabled || computedProps?.disabled"
    :style="{
      'text-transform': textTransformComputed,
    }"
  >
    <v-icon
      v-if="icon || computedProps.icon"
      :xSmall="
        iconSize === 'xSmall' ||
        (computedProps.iconSize === 'xSmall' && !size?.length)
      "
      :small="
        iconSize === 'small' ||
        (computedProps.iconSize === 'small' && !size?.length)
      "
      :large="
        iconSize === 'large' ||
        (computedProps.iconSize === 'large' && !size?.length)
      "
      :xLarge="
        iconSize === 'xLarge' ||
        (computedProps.iconSize === 'xLarge' && !size?.length)
      "
      :size="
        !['xSmall', 'small', 'medium', 'large', 'xLarge'].includes(
          iconSize || computedProps.iconSize
        )
          ? iconSize || computedProps.iconSize
          : undefined
      "
      v-html="icon ?? computedProps.icon"
    />
    <span
      v-if="label || computedProps.label"
      v-text="label ?? computedProps.label"
      :style="`${textStyle || computedProps.textStyle}`"
    />
  </v-btn>
</template>

<script>
// Libraries
import { buttonPresets } from "./buttonPresets.js";

// Components

export default {
  name: "StyledButton",
  data() {
    return {};
  },
  props: {
    icon: {
      default: undefined,
      type: [String, Object],
      required: true,
    },
    label: {
      default: "Label",
      type: String,
      required: true,
    },
    size: {
      default: "",
      type: String,
      required: false,
    },
    iconSize: {
      default: "",
      type: String,
      required: false,
    },
    action: {
      default: () => {},
      type: Function,
      required: false,
    },
    to: {
      default: () => undefined,
      type: Object,
      required: false,
    },
    fontWeight: {
      default: "bold",
      type: String,
      required: false,
    },
    color: {
      default: "",
      type: String,
      required: false,
    },
    textColor: {
      default: "review",
      type: String,
      required: false,
    },
    textTransform: {
      default: "",
      type: [String, Boolean],
      required: false,
    },
    textStyle: {
      default: "",
      type: [String, Boolean],
      required: false,
    },
    textType: {
      default: "caption",
      type: String,
      required: false,
    },
    rounded: {
      default: "",
      type: String,
      required: false,
    },
    minHeight: {
      default: undefined,
      type: [String, Number, Object],
      required: false,
    },
    height: {
      default: undefined,
      type: [String, Number, Object],
      required: false,
    },
    maxHeight: {
      default: undefined,
      type: [String, Number, Object],
      required: false,
    },
    dense: {
      default: false,
      type: Boolean,
      required: false,
    },
    outlined: {
      default: false,
      type: Boolean,
      required: false,
    },
    loading: {
      default: false,
      type: Boolean,
      required: false,
    },
    text: {
      default: false,
      type: Boolean,
      required: false,
    },
    stacked: {
      default: false,
      type: Boolean,
      required: false,
    },
    depressed: {
      default: undefined,
      type: [Boolean, Object],
      required: false,
    },
    ripple: {
      default: undefined,
      type: [Boolean, Object],
      required: false,
    },
    block: {
      default: undefined,
      type: [Boolean, Object],
      required: false,
    },
    disabled: {
      default: undefined,
      type: [Boolean, Object],
      required: false,
    },
    minWidth: {
      default: undefined,
      type: [String, Number, Object],
      required: false,
    },
    width: {
      default: undefined,
      type: [String, Number, Object],
      required: false,
    },
    maxWidth: {
      default: undefined,
      type: [String, Number, Object],
      required: false,
    },
    extraClasses: {
      default: undefined,
      type: [String, Object],
      required: false,
    },
    preset: {
      type: String,
      required: false,
      default: "",
      validator: function (value) {
        return ["", ...Object.keys(buttonPresets)].includes(value);
      },
    },
  },
  methods: {
    /* API */
    // GET
    // POST
    /* */
    handleAction() {
      if (this.action) {
        this.action();
      }
    },
  },
  computed: {
    computedProps() {
      if (!this.preset) {
        return {};
      }
      return buttonPresets[this.preset] || {};
    },
    textTransformComputed() {
      return this.textTransform === true ||
        this.computedProps.textTransform === true
        ? "uppercase"
        : "none";
    },
    widthComputed() {
      return this.width ?? this.computedProps?.width ?? undefined;
    },
    minWidthComputed() {
      return this.minWidth ?? this.computedProps?.minWidth ?? undefined;
    },
  },
};
</script>

<style scoped>
.styled-button >>> .v-btn__content {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 8px;
}

/* .styled-button.flat::before {
  opacity: 0 !important;
} */

.styled-button:before {
  background-color: currentColor;
  border-radius: inherit;
  color: inherit;
  opacity: 0;
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);
}

.stacked >>> .v-btn__content {
  display: flex !important;
  flex-direction: column !important;
  gap: 4px;
  padding-top: 8px;
  padding-bottom: 8px;
  font-size: normal !important;
}

.stacked.dense >>> .v-btn__content {
  display: flex !important;
  flex-direction: column !important;
  padding-top: 5px;
  padding-bottom: 5px;
  gap: 2px;
  font-size: 10px !important;
}
</style>
